import type { ComponentType } from "react";
import { ShieldCheck, CreditCard, Truck, Package, RefreshCw, Award, Headphones, MapPin, BadgeCheck, Wrench } from "lucide-react";

/**
 * Sitewide trust strip — four icon + label cells (garantie / paiement / livraison / N références).
 * Conversion reassurance, not prose. Content: { items: [{ icon, label, sub }] }. Full-bleed band.
 */
const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  shield: ShieldCheck,
  "shield-check": ShieldCheck,
  garantie: ShieldCheck,
  payment: CreditCard,
  "credit-card": CreditCard,
  truck: Truck,
  delivery: Truck,
  livraison: Truck,
  package: Package,
  refresh: RefreshCw,
  consigne: RefreshCw,
  award: Award,
  support: Headphones,
  headphones: Headphones,
  "map-pin": MapPin,
  garage: Wrench,
  wrench: Wrench,
  check: BadgeCheck,
};

type Item = { icon?: string; label?: string; sub?: string };

export function TrustBarRenderer({ component }: { component: { content?: { items?: Item[]; variant?: string } } }) {
  const { items = [], variant = "light" } = component.content ?? {};
  if (items.length === 0) return null;
  const dark = variant === "dark";
  return (
    <section
      className={
        dark
          ? "mx-[calc(50%-50vw)] w-screen border-y border-[#00B862]/20 bg-[#0A0A0A]"
          : "mx-[calc(50%-50vw)] w-screen border-y border-black/5 bg-white"
      }
    >
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-2 px-6 md:px-8 lg:grid-cols-4">
        {items.map((it, i) => {
          const Icon = (it.icon && ICONS[it.icon]) || ShieldCheck;
          return (
            <div key={i} className="flex items-center gap-2.5 py-3">
              <span
                className={
                  dark
                    ? "grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#00B862]/12 text-[#34D98A] ring-1 ring-[#00B862]/25"
                    : "grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--color-forest-100)] text-[#00935A]"
                }
              >
                <Icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className={dark ? "text-sm font-bold text-white" : "text-sm font-bold text-[#121212]"}>{it.label}</p>
                {it.sub ? <p className={dark ? "text-xs text-white/55" : "text-xs text-[#6B7280]"}>{it.sub}</p> : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
